import { useCallback, useRef, useState } from 'react';
import { cx } from '../primitives/cx.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';
import { IconButton } from '../primitives/Button.tsx';
import { computeQuoteTotals, lineTotal, multiplyDecimals } from '@whinerp/contracts/money';

/**
 * LineItemGrid - a quote's lines, operable entirely from the keyboard.
 *
 * The keyboard is not an accessibility afterthought here; it is how the job is
 * done. Somebody entering twenty lines from a supplier's email will not reach
 * for the mouse between cells, and a grid that makes them do so is a grid they
 * will fill in a spreadsheet and paste later.
 *
 *   Tab / Shift-Tab      move across and back through cells
 *   Enter                add a row below and land on its first cell
 *   Ctrl/Cmd-Backspace   delete the current row
 *
 * The footer updates as you type, from the same arithmetic the database uses.
 * The server's totals still win once they arrive: the optimistic figure
 * answers "what will this be", not "what is this".
 */

export interface GridLine {
  lineId: string | null;
  productId: string | null;
  description: string;
  /** Decimal strings all the way down. Money is never a float (D-018). */
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  optional: boolean;
  /** The unit the quantity is entered in (F-161). */
  unitId?: string | null;
  /** The product's base unit, which decides the units offered. */
  productBaseUnitId?: string | null;
}

export interface GridUnit {
  unitId: string;
  label: string;
  /** Null for a base unit. */
  baseUnitId: string | null;
  baseLabel: string | null;
  factor: string;
}

export interface GridUnits {
  all: GridUnit[];
  /** Header and accessible name of the unit picker. */
  label: string;
  /** "= 144 Each": what the entered quantity comes to, shown under it. */
  converted: (amount: string, baseLabel: string) => string;
}

export interface LineItemGridLabels {
  description: string;
  quantity: string;
  unitPrice: string;
  discount: string;
  lineTotal: string;
  optional: string;
  addLine: string;
  removeLine: (description: string) => string;
  /** Explains the keyboard, visibly, rather than hiding it in a tooltip. */
  keyboardHint: string;
  subtotal: string;
  optionalTotal: string;
  discountLabel: string;
  total: string;
  readOnly: string;
  /** The action column's header, announced but not drawn. */
  rowActions: string;
}

export interface LineItemGridProps {
  lines: GridLine[];
  onChange: (lines: GridLine[]) => void;
  /** Formats a decimal string as money in the quote's currency. */
  money: (amount: string) => string;
  labels: LineItemGridLabels;
  /** False once the quote is sent: the grid renders read-only, not disabled. */
  editable: boolean;
  /** The server's totals. Shown once they arrive; the optimistic ones until. */
  serverTotals?:
    | { subtotal: string; optionalTotal: string; discountAmount: string; total: string }
    | undefined;
  discountPercent: string;
  /**
   * Units of measure, when they are switched on. Absent, the grid is what it
   * always was. Present, each line offers the units its product is sold in,
   * and says under the quantity what it comes to - "silent conversion is how a
   * customer is invoiced for twelve boxes when they ordered twelve units."
   */
  units?: GridUnits | undefined;
}

/**
 * The optimistic totals, computed exactly.
 *
 * Re-exported from `@whinerp/contracts/money` rather than implemented here.
 * The first version used `Number` and `toFixed`, which the lint rule caught
 * and was right to: 1234.56 * 3 is 3703.6800000000003 in a double, and a grid
 * whose lines do not add up to its footer is a grid nobody trusts.
 */
/** The units a line may use: its product's family, or any unit for a line with no product. */
export function unitsForLine(line: GridLine, all: GridUnit[]): GridUnit[] {
  const root = line.productBaseUnitId ?? null;
  return root ? all.filter((unit) => (unit.baseUnitId ?? unit.unitId) === root) : all;
}

export function lineTotalOf(line: GridLine): string {
  return lineTotal(line);
}

export function gridTotals(
  lines: GridLine[],
  discountPercent: string,
): { subtotal: string; optionalTotal: string; discountAmount: string; total: string } {
  return computeQuoteTotals(lines, discountPercent);
}

const BLANK: GridLine = {
  lineId: null,
  productId: null,
  description: '',
  quantity: '1',
  unitPrice: '0.00',
  discountPercent: '0',
  optional: false,
};

const CELL =
  'w-full bg-transparent px-2 py-1 text-dense text-ink focus-visible:outline-none';

export function LineItemGrid({
  lines,
  onChange,
  money,
  labels,
  editable,
  serverTotals,
  discountPercent,
  units,
}: LineItemGridProps): JSX.Element {
  const gridRef = useRef<HTMLTableSectionElement>(null);
  const [focusRow, setFocusRow] = useState<number | null>(null);

  const update = useCallback(
    (index: number, patch: Partial<GridLine>) => {
      onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
    },
    [lines, onChange],
  );

  const addBelow = useCallback(
    (index: number) => {
      const next = [...lines];
      next.splice(index + 1, 0, { ...BLANK });
      onChange(next);
      setFocusRow(index + 1);
      // The new row's first cell, on the next paint. Adding a row and leaving
      // the caret where it was makes Enter feel like it did nothing.
      requestAnimationFrame(() => {
        gridRef.current
          ?.querySelector<HTMLInputElement>(
            `[data-row="${String(index + 1)}"][data-cell="description"]`,
          )
          ?.focus();
      });
    },
    [lines, onChange],
  );

  const remove = useCallback(
    (index: number) => onChange(lines.filter((_, i) => i !== index)),
    [lines, onChange],
  );

  const totals = serverTotals ?? gridTotals(lines, discountPercent);

  function onKeyDown(event: React.KeyboardEvent, index: number): void {
    if (!editable) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      addBelow(index);
    }
    if (event.key === 'Backspace' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      remove(index);
    }
  }

  const numericCell = (
    index: number,
    cell: 'quantity' | 'unitPrice' | 'discountPercent',
    label: string,
  ): JSX.Element => (
    <input
      data-row={index}
      data-cell={cell}
      inputMode="decimal"
      aria-label={`${label} ${String(index + 1)}`}
      value={lines[index]?.[cell] ?? ''}
      readOnly={!editable}
      onFocus={() => setFocusRow(index)}
      onKeyDown={(event) => onKeyDown(event, index)}
      onChange={(event) => update(index, { [cell]: event.target.value })}
      className={cx(CELL, 'text-end tabular-nums')}
    />
  );

  return (
    <div data-component="LineItemGrid" className="flex flex-col gap-2">
      {/* The keyboard, said out loud. A shortcut nobody is told about is a
          shortcut nobody uses. */}
      <p className="text-meta text-ink-meta">{editable ? labels.keyboardHint : labels.readOnly}</p>

      <div className="w-full overflow-x-auto" data-scroll-container>
        <table className="w-full min-w-panel-wide border-collapse">
          <thead>
            <tr className="border-b border-b-rule-strong">
              <th scope="col" className="px-2 py-1 text-start text-micro uppercase tracking-label text-ink-label">
                {labels.description}
              </th>
              <th scope="col" className="px-2 py-1 text-end text-micro uppercase tracking-label text-ink-label">
                {labels.quantity}
              </th>
              <th scope="col" className="px-2 py-1 text-end text-micro uppercase tracking-label text-ink-label">
                {labels.unitPrice}
              </th>
              <th scope="col" className="px-2 py-1 text-end text-micro uppercase tracking-label text-ink-label">
                {labels.discount}
              </th>
              <th scope="col" className="px-2 py-1 text-end text-micro uppercase tracking-label text-ink-label">
                {labels.lineTotal}
              </th>
              <th scope="col" className="px-2 py-1 text-start text-micro uppercase tracking-label text-ink-label">
                {labels.optional}
              </th>
              {/* Named, not blank. A header with no text leaves the column
                  unannounced, and a screen reader reads the delete buttons in
                  it as belonging to nothing. */}
              <th scope="col" className="w-touch">
                <span className="sr-only">{labels.rowActions}</span>
              </th>
            </tr>
          </thead>

          <tbody ref={gridRef}>
            {lines.map((line, index) => (
              <tr
                key={line.lineId ?? `new-${String(index)}`}
                data-line-row={index}
                className={cx('border-b border-b-rule', focusRow === index && 'bg-sunken')}
              >
                <td className="min-w-panel-narrow">
                  <input
                    data-row={index}
                    data-cell="description"
                    aria-label={`${labels.description} ${String(index + 1)}`}
                    value={line.description}
                    readOnly={!editable}
                    onFocus={() => setFocusRow(index)}
                    onKeyDown={(event) => onKeyDown(event, index)}
                    onChange={(event) => update(index, { description: event.target.value })}
                    className={CELL}
                  />
                </td>
                <td>
                  {numericCell(index, 'quantity', labels.quantity)}
                  {units ? <UnitCell line={line} index={index} units={units} editable={editable} onPick={(unitId) => update(index, { unitId })} /> : null}
                </td>
                <td>{numericCell(index, 'unitPrice', labels.unitPrice)}</td>
                <td>{numericCell(index, 'discountPercent', labels.discount)}</td>
                <td className="px-2 py-1 text-end">
                  {/* Computed, not an input. An editable cell that silently
                      overwrites itself is a cell people fight. */}
                  <Num align="end" className="text-dense text-ink">
                    <span data-line-total={index}>{money(lineTotalOf(line))}</span>
                  </Num>
                </td>
                <td className="px-2 py-1">
                  <input
                    type="checkbox"
                    data-row={index}
                    data-cell="optional"
                    aria-label={`${labels.optional} ${String(index + 1)}`}
                    checked={line.optional}
                    disabled={!editable}
                    onChange={(event) => update(index, { optional: event.target.checked })}
                    className="h-4 w-4 rounded-control border border-rule accent-bronze"
                  />
                </td>
                <td className="px-1">
                  {editable ? (
                    <IconButton
                      icon="Trash"
                      label={labels.removeLine(line.description || String(index + 1))}
                      onClick={() => remove(index)}
                    />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <td colSpan={4} className="px-2 py-1 text-end text-dense text-ink-meta">
                {labels.subtotal}
              </td>
              <td className="px-2 py-1 text-end">
                <Num align="end" className="text-dense text-ink">
                  <span data-total="subtotal">{money(totals.subtotal)}</span>
                </Num>
              </td>
              <td colSpan={2} />
            </tr>
            {Number(totals.optionalTotal) > 0 ? (
              <tr>
                <td colSpan={4} className="px-2 py-1 text-end text-dense text-ink-meta">
                  {/* Stated, never folded in: an option nobody has chosen is
                      not revenue. */}
                  {labels.optionalTotal}
                </td>
                <td className="px-2 py-1 text-end">
                  <Num align="end" className="text-dense text-ink-meta">
                    <span data-total="optional">{money(totals.optionalTotal)}</span>
                  </Num>
                </td>
                <td colSpan={2} />
              </tr>
            ) : null}
            {Number(totals.discountAmount) > 0 ? (
              <tr>
                <td colSpan={4} className="px-2 py-1 text-end text-dense text-ink-meta">
                  {labels.discountLabel}
                </td>
                <td className="px-2 py-1 text-end">
                  <Num align="end" className="text-dense text-ink">
                    <span data-total="discount">{money(totals.discountAmount)}</span>
                  </Num>
                </td>
                <td colSpan={2} />
              </tr>
            ) : null}
            <tr className="border-t border-t-rule-strong">
              <td colSpan={4} className="px-2 py-1 text-end text-body font-medium text-ink">
                {labels.total}
              </td>
              <td className="px-2 py-1 text-end">
                <Num align="end" className="text-body font-medium text-ink">
                  <span data-total="total">{money(totals.total)}</span>
                </Num>
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

      {editable ? (
        <button
          type="button"
          data-add-line
          onClick={() => addBelow(lines.length - 1)}
          className="inline-flex h-touch items-center gap-1 self-start rounded-control px-2 text-dense text-bronze-text hover:bg-sunken"
        >
          <Icon name="Plus" size="inline" decorative />
          {labels.addLine}
        </button>
      ) : null}
    </div>
  );
}

function UnitCell({
  line,
  index,
  units,
  editable,
  onPick,
}: {
  line: GridLine;
  index: number;
  units: GridUnits;
  editable: boolean;
  onPick: (unitId: string | null) => void;
}): JSX.Element | null {
  const options = unitsForLine(line, units.all);
  const chosen =
    units.all.find((unit) => unit.unitId === line.unitId) ??
    units.all.find((unit) => unit.unitId === line.productBaseUnitId) ??
    null;
  if (!chosen && options.length === 0) return null;
  const base = chosen ? (chosen.baseLabel ?? chosen.label) : null;
  let converted: string | null = null;
  if (chosen && base && chosen.factor !== '1') {
    try {
      converted = multiplyDecimals(line.quantity || '0', chosen.factor, 3).replace(/\.?0+$/, '');
    } catch {
      converted = null;
    }
  }
  return (
    <div className="flex flex-col items-end gap-0.5 px-2 pb-1">
      {editable && options.length > 1 ? (
        <select
          data-row={index}
          data-cell="unit"
          aria-label={`${units.label} ${String(index + 1)}`}
          value={chosen?.unitId ?? ''}
          onChange={(event) => onPick(event.target.value || null)}
          className="rounded-control border border-control bg-surface px-1 text-meta text-ink"
        >
          {options.map((unit) => (
            <option key={unit.unitId} value={unit.unitId}>
              {unit.label}
            </option>
          ))}
        </select>
      ) : chosen ? (
        <span data-line-unit={index} className="text-meta text-ink-600">
          {chosen.label}
        </span>
      ) : null}
      {converted !== null && base ? (
        <span data-line-converted={index} className="text-meta text-ink-meta">
          {units.converted(converted, base)}
        </span>
      ) : null}
    </div>
  );
}
